import React, { useState } from 'react';
import { Col, Row } from 'antd';
import {
  MapPinIcon,
  Clock2Icon,
  PhoneIcon,
  ArrowDownIcon,
  ChevronUpIcon,
  NavigationIcon,
  ArrowUpRightIcon,
  // CrownIcon,
} from 'assets/svg';
import styled from 'styled-components';
import { Rate } from 'antd';
import classnames from 'classnames';
import { breakpoints } from 'constant/style';
import Button from 'components/Button';
import { SCHEDULE_DAYS_NUMBER, ThirdPartyLinksType, ThirdPartyLinksTypeQuery } from 'constant/constants';
import router, { useRouter } from 'next/router';
import { getServiceOptionsLabels } from 'utils/common';
import moment from 'moment';
import { PARTNER, PARTNER_NAMES } from 'constant/partner';
import { getOperationalHours } from 'utils/schedule';
import capitalize from 'lodash/capitalize';
import * as fbq from 'lib/fpixel';
import * as ga from 'lib/ga';
import { TRACKING } from 'constant/analytics';
import { OpeningHourType, OperatingHourType, Outlet } from 'utils/types';
import { DISTANCE_UNIT, LOCATION_STATUS, LOCATION_STATUS_INFO_TAG } from 'constant';
import { isEmpty } from 'lodash';

interface OutletsProps {
  data: Outlet[];
  category?: string;
  enableHover?: boolean;
  [x: string]: any;
}

export default function Outlets({ data, category, enableHover = true, ...props }: OutletsProps) {
  const [expandOperatingHours, setExpandOperatingHours] = useState<Record<string, boolean> | null>(null);
  // const [expandSpecialHours, setExpandSpecialHours] = useState<Record<string, boolean> | null>(null);
  const { query } = useRouter();

  const { slug } = query;

  const hasThirdPartyLinks = (location: Outlet, type: string) => {
    if (category) {
      return false;
    }
    if (location.thirdPartyLinks && location.thirdPartyLinks.length > 0) {
      return location.thirdPartyLinks.some((o) => o.type === type);
    }
  };

  const navigateToLocationCategories = (
    event: React.MouseEvent<HTMLElement, MouseEvent>,
    type: string,
    locationId: string,
  ) => {
    event.stopPropagation();
    router.push(`/${slug}/locations/${locationId}/${type}`);
  };

  const getPlatformNameForOpHour = (openingHours: OpeningHourType[]) => {
    let platform = '';

    if (openingHours && openingHours.length > 0) {
      if (category) {
        switch (category) {
          case 'GrabFood':
            platform = PARTNER_NAMES[PARTNER.GRAB];
            break;

          case 'Foodpanda':
            platform = PARTNER_NAMES[PARTNER.FOODPANDA];
            break;

          case 'Deliveroo':
            platform = PARTNER_NAMES[PARTNER.DELIVEROO];
            break;

          default:
            platform = openingHours[0].platform;
            break;
        }
      } else {
        if (openingHours.some((o) => o.platform === PARTNER_NAMES[PARTNER.GMB])) {
          platform = PARTNER_NAMES[PARTNER.GMB];
        } else {
          platform = openingHours[0].platform;
        }
      }
    }

    return platform;
  };

  const hasPlatformHours = (hours: OpeningHourType[] = []) => {
    return hours.some((o) => o.platform === getPlatformNameForOpHour(hours));
  };

  const sort_days = (list: any) => {
    const day_of_week = new Date().getDay();
    const indexOfDay = list.findIndex((o: any) => o.day === SCHEDULE_DAYS_NUMBER[day_of_week]);
    return list.slice(indexOfDay).concat(list.slice(0, indexOfDay));
  };

  const getPlatformOpeningHours = (openingHours: OpeningHourType[]) => {
    const platformOpHours: OpeningHourType[] = openingHours.filter(
      (o) => o.platform === getPlatformNameForOpHour(openingHours),
    );

    let formattedPlatformOpHours: OperatingHourType[] = getOperationalHours(platformOpHours);

    if (formattedPlatformOpHours && formattedPlatformOpHours.length > 0) {
      formattedPlatformOpHours = sort_days(formattedPlatformOpHours);
    }

    return formattedPlatformOpHours;
  };

  // const getPlatformSpecialHours = (hours: OpeningHourType[] = []) => {
  //   const platformOpHours = hours.filter((o) => o.platform === getPlatformNameForOpHour(hours));

  //   return getSpecialHours(platformOpHours);
  // };

  const getOperatingHourNode = (openingHours: OpeningHourType[], locationId: string) => {
    const day = new Date().getDay();

    const openingHoursData: typeof openingHours = openingHours.filter(
      (o) => o.platform === getPlatformNameForOpHour(openingHours),
    );

    const formattedOpeningHoursData: OperatingHourType[] = getOperationalHours(openingHoursData);

    const formattedOperatingHoursData: OperatingHourType | undefined =
      formattedOpeningHoursData && formattedOpeningHoursData.find((o) => o.day === SCHEDULE_DAYS_NUMBER[day]);
    // If there is no start time & end time then hide hours
    if (formattedOperatingHoursData && formattedOperatingHoursData?.startTime && formattedOperatingHoursData?.endTime) {
      if (formattedOperatingHoursData.isOpen) {
        const lastSchedule = formattedOperatingHoursData.dataList?.[formattedOperatingHoursData?.dataList?.length - 1];
        if (lastSchedule) {
          if (lastSchedule.endTime && moment(lastSchedule.endTime, 'HH:mm').isBefore(moment(new Date(), 'HH:mm'))) {
            return <p style={{ color: 'var(--orange)' }}>Closed</p>;
          }
        } else {
          if (moment(formattedOperatingHoursData.endTime, 'HH:mm').isBefore(moment(new Date(), 'HH:mm'))) {
            return <p style={{ color: 'var(--orange)' }}>Closed</p>;
          }
        }

        return (
          <p>
            <span style={{ color: 'var(--green)' }}>Open</span>
            {(!expandOperatingHours || !expandOperatingHours[locationId]) && (
              <>
                <span> - </span>
                <span>
                  Closes{' '}
                  {moment(lastSchedule ? lastSchedule.endTime : formattedOperatingHoursData.endTime, 'HH:mm').format(
                    'hh:mm A',
                  )}
                </span>
              </>
            )}
          </p>
        );
      }
      return <p style={{ color: 'var(--orange)' }}>Closed</p>;
    }
    return null;
  };

  const onExpandOperatingHours = (event: React.MouseEvent<HTMLDivElement, MouseEvent>, id: string) => {
    event.stopPropagation();
    event.preventDefault();
    setExpandOperatingHours({
      ...expandOperatingHours,
      [id]: expandOperatingHours?.[id] ? false : true,
    });
  };
  // Commenting for now but might need later
  // const onExpandSpecialHours = (event: React.MouseEvent<HTMLDivElement, MouseEvent>, id: string) => {
  //   event.stopPropagation();
  //   event.preventDefault();
  //   setExpandSpecialHours({
  //     ...expandSpecialHours,
  //     [id]: expandSpecialHours?.[id] ? false : true,
  //   });
  // };

  return (
    <OutletsWrap minHeight={props.minHeight} enableHover={enableHover}>
      <Row gutter={[16, 16]}>
        {data?.map((location, key) => {
          const hasFoodDeliveryThirdPartyLinks = hasThirdPartyLinks(location, ThirdPartyLinksType.FOOD_DELIVERY);
          const hasReservationThirdPartyLinks = hasThirdPartyLinks(location, ThirdPartyLinksType.RESERVATION);
          const hasGmbReviewLinks = !isEmpty(location.gmbReviewLink);
          const addrObj = {
            address1: location?.address1,
            address2: location?.address2 || '',
            city: location?.city,
            pinCode: location?.pinCode || '',
          };

          // const platformSpecialHours: SpecialHourType[] = getPlatformSpecialHours(location?.specialHours);
          return (
            <Col lg={12} xl={8} xxl={6} xs={24} key={key}>
              <a
                className="location-item"
                href={category ? location?.thirdPartyLinks?.[0]?.link : undefined}
                target="_blank"
                rel="noreferrer"
                onClick={() => {
                  ga.event({
                    ...TRACKING.OPEN_PLATFROM,
                    value: category ? location?.thirdPartyLinks?.[0]?.link : null,
                  });
                }}
              >
                <div className="location-content">
                  <div className="card-header">
                    <div className="location-title-wrap">
                      <h5>{location.name}</h5>
                      {category && (
                        <a
                          className="round-icon-wrap"
                          href={location?.thirdPartyLinks?.[0]?.link}
                          target="_blank"
                          rel="noreferrer"
                        >
                          <ArrowUpRightIcon />
                        </a>
                      )}
                    </div>

                    {location.count > 0 && !category && (
                      <div className="rating-wrap">
                        <div className="body-text mr-15">{location.avgRating}</div>
                        <div className="flex mr-15">
                          <Rate disabled allowHalf defaultValue={location?.avgRating} style={{ fontSize: 14 }} />
                        </div>
                        <a className="body-text underline" href={location.mapUrl} target="_blank" rel="noreferrer">
                          {location.count} Reviews
                        </a>
                      </div>
                    )}
                  </div>

                  <div className="card-body">
                    {!category && (
                      <div className="service-options-wrap mb-20">
                        {getServiceOptionsLabels(location?.serviceOptions)?.map((option, key) => (
                          <div key={key} className="service-option body-text">
                            {option}
                          </div>
                        ))}
                      </div>
                    )}

                    <div className="location-info">
                      {location.address1 && (
                        <div className="location-address-distance-wrap">
                          <a className="location-meta" href={location.mapUrl} target="_blank" rel="noreferrer">
                            <div className="icon-wrap">
                              <MapPinIcon width={27.19} height={21} />
                            </div>
                            <p>
                              {Object.values(addrObj)
                                ?.filter((val) => val)
                                ?.join(', ')}
                            </p>
                          </a>
                          {location?.distance && !isNaN(location?.distance) ? (
                            <div className="navigation-icon-wrap">
                              <NavigationIcon width={14} height={14} />
                              <div className="body-text distance-text">
                                {location?.distance?.toFixed(1)} {DISTANCE_UNIT}
                              </div>
                            </div>
                          ) : null}
                        </div>
                      )}

                      {getPlatformNameForOpHour(location?.openingHours) === PARTNER_NAMES[PARTNER.GMB] &&
                      location?.gmbFkLocationStatus &&
                      (location.gmbFkLocationStatus == LOCATION_STATUS.CLOSED_TEMPORARILY ||
                        location.gmbFkLocationStatus == LOCATION_STATUS.CLOSED_PERMANENTLY) ? (
                        <div className="location-meta">
                          <div className="icon-wrap">
                            <Clock2Icon width={20} height={21} />
                          </div>
                          <div className="flex-1">
                            <div className="outlet-status">
                              <p>
                                <span style={{ color: 'var(--yellow-dark)' }}>
                                  {
                                    LOCATION_STATUS_INFO_TAG[
                                      location.gmbFkLocationStatus as keyof typeof LOCATION_STATUS_INFO_TAG
                                    ]
                                  }
                                </span>
                              </p>
                            </div>
                          </div>
                        </div>
                      ) : (
                        hasPlatformHours(location?.openingHours) &&
                        getOperatingHourNode(location?.openingHours, location.id) && (
                          <div className="location-meta">
                            <div className="icon-wrap">
                              <Clock2Icon width={20} height={21} />
                            </div>
                            <div className="flex-1">
                              <div
                                className="outlet-status"
                                onClick={(event) => onExpandOperatingHours(event, location.id)}
                              >
                                <div>{getOperatingHourNode(location?.openingHours, location.id)}</div>
                                <div className="collapse-icon">
                                  {expandOperatingHours && expandOperatingHours[location.id] ? (
                                    <ChevronUpIcon />
                                  ) : (
                                    <ArrowDownIcon />
                                  )}
                                </div>
                              </div>
                              {expandOperatingHours && expandOperatingHours[location.id] && (
                                <div className="opening-hours mt-20">
                                  {getPlatformOpeningHours(location?.openingHours).map((opHour, key) => (
                                    <React.Fragment key={key}>
                                      <div className="schedule-wrap">
                                        <div
                                          className={classnames('body-text', {
                                            'text-bold': key === 0,
                                          })}
                                        >
                                          {capitalize(opHour.day)}
                                        </div>
                                        <div className={classnames('hours-wrap body-text', { 'text-bold': key === 0 })}>
                                          {opHour.isOpen
                                            ? `${moment(opHour.startTime, 'HH:mm').format('hh:mm A')} - ${moment(
                                                opHour.endTime,
                                                'HH:mm',
                                              ).format('hh:mm A')}`
                                            : 'Closed'}
                                        </div>
                                      </div>
                                      {opHour.dataList?.map((opSameDayHour: any, subKey: number) => (
                                        <div className="schedule-wrap" key={subKey}>
                                          <div />
                                          <div className="hours-wrap body-text">
                                            {`${moment(opSameDayHour.startTime, 'HH:mm').format('hh:mm A')} - ${moment(
                                              opSameDayHour.endTime,
                                              'HH:mm',
                                            ).format('hh:mm A')}`}
                                          </div>
                                        </div>
                                      ))}
                                    </React.Fragment>
                                  ))}
                                </div>
                              )}
                            </div>
                          </div>
                        )
                      )}
                      {/* Hide Special hours now based on feedback, might need later, that's why kept it*/}
                      {/* {hasPlatformHours(location?.specialHours) && (
                        <div className="location-meta">
                          <div className="icon-wrap">
                            <CrownIcon width={20} height={21} />
                          </div>
                          <div className="flex-1">
                            <div
                              className="outlet-status"
                              onClick={(event) => onExpandSpecialHours(event, location.id)}
                            >
                              <div className="text-grey-dark">Special hours</div>
                              {platformSpecialHours[0]?.isOpen && (
                                <span
                                  style={{
                                    color: 'var(--green)',
                                    marginLeft: 8,
                                  }}
                                >
                                  {moment(platformSpecialHours[0].startTime, 'HH:mm').format('hh:mm A')} -{' '}
                                  {moment(platformSpecialHours[0].endTime, 'HH:mm').format('hh:mm A')}
                                </span>
                              )}
                              <div className="collapse-icon">
                                {expandSpecialHours && expandSpecialHours[location.id] ? (
                                  <ChevronUpIcon />
                                ) : (
                                  <ArrowDownIcon />
                                )}
                              </div>
                            </div>
                            {expandSpecialHours && expandSpecialHours[location.id] && (
                              <div className="opening-hours mt-20">
                                {platformSpecialHours.map((opHour, key) => (
                                  <React.Fragment key={key}>
                                    <div className="schedule-wrap flex">
                                      <div className={classnames('body-text min-w-6', { 'text-bold': key === 0 })}>
                                        {moment(opHour.day).format('dddd')}
                                      </div>
                                      <div
                                        className={classnames('body-text text-center', {
                                          'text-bold': key === 0,
                                        })}
                                      >
                                        {moment(opHour.day).format('DD.MM.YYYY')}
                                      </div>
                                      <div className={classnames('hours-wrap body-text', { 'text-bold': key === 0 })}>
                                        {opHour.isOpen
                                          ? `${moment(opHour.startTime, 'HH:mm').format('hh:mm A')} - ${moment(
                                              opHour.endTime,
                                              'HH:mm',
                                            ).format('hh:mm A')}`
                                          : 'Closed'}
                                      </div>
                                    </div>
                                  </React.Fragment>
                                ))}
                              </div>
                            )}
                          </div>
                        </div>
                      )} */}
                      {location.contactInfo?.phone && (
                        <a
                          className="location-meta"
                          href={`tel:${location.contactInfo?.pin} ${location.contactInfo?.phone}`}
                        >
                          <div className="icon-wrap">
                            <PhoneIcon width={20} height={21} />
                          </div>
                          <p>
                            {location.contactInfo?.pin} {location.contactInfo?.phone}
                          </p>
                        </a>
                      )}
                    </div>
                  </div>
                </div>

                <Row gutter={[16, 16]}>
                  {hasReservationThirdPartyLinks && (
                    <Col>
                      <Button
                        block
                        size="middle"
                        shape="round"
                        onClick={(event) => {
                          fbq.buttonClickEvent({ content_name: 'Reservation' });
                          ga.event({
                            ...TRACKING.LOCATION.ORDER_FOR_DELIVERY,
                            value: location?.id,
                          });
                          navigateToLocationCategories(event, ThirdPartyLinksTypeQuery.RESERVATION, location?.id);
                        }}
                      >
                        Make a Reservation
                      </Button>
                    </Col>
                  )}
                  {hasFoodDeliveryThirdPartyLinks && (
                    <Col>
                      <Button
                        block
                        size="middle"
                        shape="round"
                        onClick={(event) => {
                          fbq.buttonClickEvent({ content_name: 'Delivery' });
                          ga.event({
                            ...TRACKING.LOCATION.MAKE_A_RESERVATION,
                            value: location?.id,
                          });
                          navigateToLocationCategories(event, ThirdPartyLinksTypeQuery.FOOD_DELIVERY, location?.id);
                        }}
                      >
                        Order
                      </Button>
                    </Col>
                  )}
                  {hasGmbReviewLinks && (
                    <Col>
                      <Button
                        block
                        size="middle"
                        shape="round"
                        onClick={() => {
                          fbq.buttonClickEvent({ content_name: 'Review us' });
                          ga.event({
                            ...TRACKING.LOCATION.OPEN_A_PLATFORM,
                            value: location?.id,
                          });
                          window.open(location.gmbReviewLink, '_blank', 'noopener,noreferrer');
                        }}
                      >
                        Review us
                      </Button>
                    </Col>
                  )}
                </Row>
              </a>
            </Col>
          );
        })}
      </Row>
    </OutletsWrap>
  );
}

interface OutletsWrapProps {
  minHeight: string;
  enableHover: boolean;
}

const OutletsWrap = styled.div<OutletsWrapProps>`
  margin-bottom: 20px;
  .text-grey-dark {
    color: var(--grey-dark);
  }
  .flex {
    display: flex;
    justify-content: space-between;
    align-items: center;
  }

  .min-w-6 {
    min-width: 6rem;
  }

  .location-item {
    border: 1px solid var(--grey-light);
    padding: 10px;
    border-radius: 10px;
    cursor: pointer;
    display: flex;
    flex-direction: column;
    min-height: ${(props) => props.minHeight || '280px'};
    transition: all 300ms ease 0s;

    ${({ enableHover }) =>
      enableHover &&
      `
      :hover {
        h5 {
          color: var(--orange);
        }

        .round-icon-wrap {
          background: var(--orange) !important;

          svg  {
            path {
              stroke: var(--white);
            }
          }
        }
      }
    `}

    .location-content {
      flex: 1;
    }

    .card-header {
      padding: 10px 0px;
      border-bottom: 1px solid var(--grey-light);

      .location-title-wrap {
        display: flex;
        flex-direction: row;
        align-items: center;
        margin-bottom: 10px;

        h5 {
          margin-right: 10px;
          margin-bottom: 0px;
          flex: 1;
        }

        .round-icon-wrap {
          width: 30px;
          height: 30px;
          border-radius: 50%;
          background: var(--white);
          box-shadow: 0px 4px 19px rgba(0, 0, 0, 0.07);
          display: flex;
          align-items: center;
          justify-content: center;
          transition: all 300ms ease 0s;
        }
      }

      .rating-wrap {
        display: flex;
        align-items: center;
        color: var(--grey-dark);
      }
    }

    .card-body {
      padding: 10px 0px;
    }

    .service-options-wrap {
      display: flex;

      .service-option {
        margin-right: 10px;
        align-items: center;
        display: flex;
        color: var(--orange);
        background: rgba(216, 67, 57, 0.08);
        padding: 5px 10px;
        border-radius: 500px;
      }
    }

    .location-info {
      margin-top: 15px;

      .location-address-distance-wrap {
        display: flex;
        flex-direction: row;
        align-items: center;
      }

      .navigation-icon-wrap {
        display: flex;
        flex-direction: row;
        align-items: center;
        margin-bottom: 15px;
        padding-left: 10px;

        svg {
          margin-right: 5px;
        }
      }

      .location-meta {
        display: flex;
        flex-direction: row;
        margin-bottom: 15px;
        flex: 1;

        .outlet-status {
          display: flex;
          flex-direction: row;
          align-items: center;
        }

        .opening-hours {
          flex: 1;

          .schedule-wrap {
            display: flex;
            flex-direction: row;
            margin-bottom: 10px;

            .hours-wrap {
              flex: 1;
              justify-content: flex-end;
              display: flex;
            }
          }
        }

        .icon-wrap {
          width: 10%;
          display: flex;

          @media (min-width: ${breakpoints.desktop}) {
            width: 5%;
          }

          & > svg path {
            stroke: var(--grey-dark);
          }
        }

        .collapse-icon {
          display: flex;
          align-items: center;
          justify-content: center;
          margin-left: 10px;
        }

        p {
          margin-bottom: 0px;
        }
      }
    }

    .location-action-wrap {
      display: flex;
      flex-direction: row;
      justify-content: space-between;

      > button {
        margin-right: 10px;
      }
    }
  }
`;
